export type CorporateHeroTemplateType =
  | 'classic-gradient'
  | 'split-text-image'
  | 'image-overlay'
  | 'minimal-centered'
  | 'two-column-text'
  | 'three-column-stats'
  | 'cta-feature'
  | 'accent-split-title'
  | 'color-wash';

export type TitleColorMode = 'solid' | 'accent-split';

export type CorporateHeroAnimationType =
  | 'none'
  | 'fade-up'
  | 'slide-left'
  | 'slide-right'
  | 'scale-on-scroll';

export type CorporateHeroHighlightAnimationType =
  | 'none'
  | 'pulse'
  | 'glow'
  | 'underline'
  | 'fade-in';

export const CORPORATE_HERO_HIGHLIGHT_ANIMATIONS: CorporateHeroHighlightAnimationType[] = [
  'none',
  'pulse',
  'glow',
  'underline',
  'fade-in',
];

export type CorporateHeroTextAlign = 'left' | 'center' | 'right';

export type CorporateHeroButtonAction =
  | 'scroll-to-section'
  | 'external-url'
  | 'internal-page'
  | 'contact-page'
  | 'products-page'
  | 'none';

export interface CorporateHeroColumnContent {
  badge?: string;
  title?: string;
  description?: string;
}

export interface CorporateHeroStatItem {
  value?: string;
  label?: string;
  icon?: string;
}

export interface CorporateHeroButtonItem {
  label?: string;
  action?: CorporateHeroButtonAction;
  /** scroll-to-section: element id; internal-page / external-url: href */
  target?: string;
  enabled?: boolean;
  /** color-wash: primary = dolu, outline = çerçeve */
  variant?: 'primary' | 'outline';
}

export interface CorporateHeroSlideItem {
  imageUrl?: string;
  title?: string;
}

/** Hero butonlarında "bölüme kaydır" için seçenek (etiket = başlık, value = DOM id) */
export interface CorporateHeroSectionOption {
  label: string;
  value: string;
}

export interface CorporateHeroDesignSettings {
  titleColor?: string;
  descriptionColor?: string;
  /** solid = tek renk; accent-split = seçili kelime site birincil renginde */
  titleColorMode?: TitleColorMode;
  /** Vurgulanacak kelime/ifade; veya başlıkta [[kelime]] kullanın */
  highlightText?: string;
  /** Vurgu rengi; boşsa site birincil rengi kullanılır */
  highlightTextColor?: string;
  /** Vurgu yazı boyutu (px). Boşsa başlık boyutunu miras alır. */
  highlightFontSize?: number;
  /** Vurgulu kelime animasyonu */
  highlightAnimation?: CorporateHeroHighlightAnimationType;
  /** Başlık font boyutu (px). Boşsa şablonun varsayılan boyutu kullanılır. */
  titleFontSize?: number;
  /** Başlık yazı tipi. Boşsa şablonun varsayılanı. */
  titleFontFamily?: string;
  titleBold?: boolean;
  titleItalic?: boolean;
  badgeText?: string;
  badgeColor?: string;
  badgeTextColor?: string;
  textAlign?: CorporateHeroTextAlign;
  overlayColor?: string;
  overlayOpacity?: number;
  animationType?: CorporateHeroAnimationType;
  animationDurationMs?: number;
  /** Başlık/açıklama yazı efekti (TAE maske, fade, satır) */
  titleRevealEffect?: import('../utils/titleRevealEffect').TitleRevealEffect;
  /** color-wash hero: siyah veya beyaz zemin */
  surfaceTone?: 'dark' | 'light';
}

export interface CorporateHeroLayoutSettings {
  imageUrl?: string;
  imagePosition?: 'left' | 'right';
  /** color-wash: görsel yok, yanda veya metnin altında */
  imagePlacement?: 'none' | 'left' | 'right' | 'below';
  backgroundImageUrl?: string;
  leftColumn?: CorporateHeroColumnContent;
  rightColumn?: CorporateHeroColumnContent;
  stats?: CorporateHeroStatItem[];
  /** Görsel CTA butonları (Ücretsiz Deneyin vb.) */
  showPrimaryButtons?: boolean;
  primaryButtons?: CorporateHeroButtonItem[];
  /** Alt metin navigasyon butonları */
  showSecondaryButtons?: boolean;
  secondaryButtons?: CorporateHeroButtonItem[];
  /** Alt minimal istatistik şeridi */
  showStatsStrip?: boolean;
  /** Sağ/sol minimal görsel slider */
  showImageSlider?: boolean;
  slides?: CorporateHeroSlideItem[];
  sliderAutoplayMs?: number;
}

export interface CorporateHeroSettingsJson {
  templateType?: CorporateHeroTemplateType;
  /** Hero bölümünün sitede gösterilip gösterilmeyeceği (varsayılan: true) */
  enabled?: boolean;
  design?: CorporateHeroDesignSettings;
  layout?: CorporateHeroLayoutSettings;
}

export interface CorporateHeroSectionSettings {
  title: string;
  description: string;
  backgroundColor: string;
  height: number;
  /** false ise hero sitede gizlenir */
  enabled: boolean;
  templateType: CorporateHeroTemplateType;
  settingsJson?: CorporateHeroSettingsJson | string | null;
  design: CorporateHeroDesignSettings;
  layout: CorporateHeroLayoutSettings;
}

export interface CorporateHeroTemplateMeta {
  id: CorporateHeroTemplateType;
  label: string;
  description: string;
  previewHint: string;
}

export const CORPORATE_HERO_DEFAULT_TEMPLATE: CorporateHeroTemplateType = 'accent-split-title';

export const CORPORATE_HERO_MAX_PRIMARY_BUTTONS = 2;
export const CORPORATE_HERO_MAX_SECONDARY_BUTTONS = 8;
export const CORPORATE_HERO_MAX_SLIDES = 6;
export const CORPORATE_HERO_MAX_STATS_STRIP = 4;

export const CORPORATE_HERO_TEMPLATE_CATALOG: CorporateHeroTemplateMeta[] = [
  {
    id: 'classic-gradient',
    label: 'Klasik Gradient',
    description: 'Ortalanmış başlık ve gradient arka plan',
    previewHint: 'Gradient + Orta Metin',
  },
  {
    id: 'cta-feature',
    label: 'CTA & Özellikler',
    description: 'Görsel butonlar, alt menü, istatistik şeridi ve opsiyonel slider',
    previewHint: 'CTA + Nav + Stats + Slider',
  },
  {
    id: 'split-text-image',
    label: 'Metin & Görsel',
    description: 'Yarı görsel (sol), yarı metin split layout',
    previewHint: 'Görsel Sol + Metin',
  },
  {
    id: 'image-overlay',
    label: 'Görsel Overlay',
    description: 'Tam genişlik arka plan görseli üzerinde metin',
    previewHint: 'BG Image + Overlay',
  },
  {
    id: 'minimal-centered',
    label: 'Minimal Ortalanmış',
    description: 'Sade arka plan ve ortalanmış tipografi',
    previewHint: 'Solid + Center',
  },
  {
    id: 'two-column-text',
    label: 'İki Sütun Metin',
    description: 'Üstte başlık, altta iki sütun metin bloğu',
    previewHint: 'Başlık + 2 Kolon',
  },
  {
    id: 'three-column-stats',
    label: 'Üç Sütun İstatistik',
    description: 'Başlık altında üç istatistik sütunu',
    previewHint: '3 Stat Kolon',
  },
  {
    id: 'accent-split-title',
    label: 'İki Renkli Başlık',
    description: 'Sol hizalı başlık; seçilen kelime site birincil renginde',
    previewHint: 'Accent Split Title',
  },
];

export const defaultCorporateHeroSectionSettings: CorporateHeroSectionSettings = {
  title: '',
  description: '',
  backgroundColor: 'linear-gradient(120deg, #e8f2fa 0%, #f7fbff 45%, #ffffff 100%)',
  height: 500,
  enabled: true,
  templateType: CORPORATE_HERO_DEFAULT_TEMPLATE,
  settingsJson: null,
  design: {
    animationType: 'fade-up',
    animationDurationMs: 600,
    titleRevealEffect: 'word-mask-up',
    textAlign: 'left',
  },
  layout: {
    imagePosition: 'right',
    stats: [],
    showPrimaryButtons: false,
    showSecondaryButtons: false,
    showStatsStrip: false,
    showImageSlider: false,
    primaryButtons: [],
    secondaryButtons: [],
    slides: [],
    sliderAutoplayMs: 5000,
  },
};
